import type { MenuItemType } from "#src/layout/layout-menu/types";
import type { AppRouteRecordRaw } from "#src/router/types";

import { rootRoute, router } from "#src/router";
import { ROOT_ROUTE_ID } from "#src/router/constants";
import { baseRoutes } from "#src/router/routes";
import { ascending } from "#src/router/utils/ascending";
import { flattenRoutes } from "#src/router/utils/flatten-routes";
import { generateMenuItemsFromRoutes } from "#src/router/utils/generate-menu-items-from-routes";
import { create } from "zustand";

interface AccessState {
	// Route menu
	wholeMenus: MenuItemType[]
	// Authorized React Router routes
	routeList: AppRouteRecordRaw[]
	// Flattened routes, with route id as the index key
	flatRouteList: Record<string, AppRouteRecordRaw>
	// Whether access has been checked
	isAccessChecked: boolean
}

// Move baseRoutes initialization inside a function to avoid circular dependency
function getInitialState(): AccessState {
	const initialBaseRoutes = baseRoutes || [];
	return {
		wholeMenus: generateMenuItemsFromRoutes(initialBaseRoutes),
		routeList: initialBaseRoutes,
		flatRouteList: flattenRoutes(initialBaseRoutes),
		isAccessChecked: false,
	};
}

interface AccessAction {
	setAccessStore: (routes: AppRouteRecordRaw[]) => AccessState
	reset: () => void
};

export const useAccessStore = create<AccessState & AccessAction>(set => ({
	// Initialize with empty state first, then update
	wholeMenus: [],
	routeList: [],
	flatRouteList: {},
	isAccessChecked: false,

	setAccessStore: (routes) => {
		const initialBaseRoutes = baseRoutes || [];
		const newRoutes = ascending([...initialBaseRoutes, ...routes]);
		/* Add new routes to the root route */
		router.patchRoutes(ROOT_ROUTE_ID, routes);
		const flatRouteList = flattenRoutes(newRoutes);
		const wholeMenus = generateMenuItemsFromRoutes(newRoutes);
		const newState = {
			wholeMenus,
			routeList: newRoutes,
			flatRouteList,
			isAccessChecked: true,
		};
		set(() => newState);
		return newState;
	},

	reset: () => {
		/* Remove dynamic routes */
		router._internalSetRoutes(rootRoute);
		const initialState = getInitialState();
		set(initialState);
	},
}));

// // Initialize the store after creation
// // This is a workaround for circular dependencies
// setTimeout(() => {
// 	const store = useAccessStore.getState();
// 	if (store.routeList.length === 0) {
// 		store.setAccessStore([]);
// 	}
// }, 0);
